// NOTE: MIME allow-list for knowledge-base uploads. Web uses it as the
// `<input type="file" accept="...">` filter, mobile as the
// `DocumentPicker.getDocumentAsync({ type })` filter — keep it here so the two
// platforms can't drift (e.g. NSAI-2796 added legacy `application/msword`,
// which browsers report for `.doc`).
export const KNOWLEDGE_ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/markdown",
  "text/x-markdown",
  "text/csv",
  "application/json",
] as const;

// NOTE: Canonical extension list rendered under the upload control ("PDF, DOC,
// ..."). One entry per format — the MIME list above carries the variants.
export const KNOWLEDGE_SUPPORTED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "pptx",
  "xlsx",
  "txt",
  "md",
  "csv",
  "json",
] as const;

// NOTE: Aliases a picker may report for a format the server knows under a
// different name. iOS `DocumentPicker` answers `text/x-markdown` for `.md` —
// the preferred MIME of Apple's `net.daringfireball.markdown` UTI — while the
// server's allow-list only carries `text/markdown`, so an un-normalised value
// is rejected with `File type "text/x-markdown" is not supported.` The alias
// list above (`KNOWLEDGE_ACCEPTED_MIME_TYPES`) keeps `text/x-markdown` so the
// picker still offers `.md` files; this map is what turns the picked value
// into something the server accepts before upload.
export const KNOWLEDGE_MIME_ALIASES = {
  "text/x-markdown": "text/markdown",
} as const satisfies Record<
  string,
  (typeof KNOWLEDGE_ACCEPTED_MIME_TYPES)[number]
>;

// NOTE: Extension → MIME fallback for pickers that do not report a usable MIME
// type. Android document providers routinely hand back
// `application/octet-stream` (or nothing) for `.md`, `.json` and legacy Office
// files, which would otherwise fail the allow-list check above even though the
// server accepts them. The `satisfies` clause is the anti-drift guard: it fails
// the build if an extension above loses its mapping, or if a mapping points at
// a MIME type that is not on the allow-list.
export const KNOWLEDGE_EXTENSION_MIME_MAP = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  json: "application/json",
} as const satisfies Record<
  (typeof KNOWLEDGE_SUPPORTED_EXTENSIONS)[number],
  (typeof KNOWLEDGE_ACCEPTED_MIME_TYPES)[number]
>;

// NOTE: React Query cache keys for the knowledge endpoints. They live here
// rather than next to a hook because both platforms key the same endpoints, and
// most of the app already imports its query keys from this package (see
// ASSISTANTS_QUERY_KEY, CHAT_SETTINGS_QUERY_KEY, USER_QUERY_KEY).
export const KNOWLEDGE_FILES_QUERY_KEY = "knowledge-files";
export const KNOWLEDGE_FILE_SHARES_QUERY_KEY = "knowledgeFileShares";
export const KNOWLEDGE_SHARED_WITH_ME_QUERY_KEY = "knowledgeSharedWithMe";

// NOTE: Ingestion is async on the backend, so the file list is polled at this
// interval while anything is still pending/processing. Same cadence as the web
// `useGetKnowledgeFiles`.
export const KNOWLEDGE_POLLING_INTERVAL_MS = 3000;

// NOTE: Semantic-search query bounds, shared by the web search dialog and the
// mobile search modal so both reject the same inputs before hitting the API.
export const KNOWLEDGE_SEARCH_QUERY_MIN_LENGTH = 2;
export const KNOWLEDGE_SEARCH_QUERY_MAX_LENGTH = 5000;

// NOTE: Search-result snippet sizing — how much of a chunk is shown collapsed,
// and how much context is kept before the first match.
export const KNOWLEDGE_CONTENT_PREVIEW_LENGTH = 200;
export const KNOWLEDGE_CONTEXT_PADDING = 80;

// NOTE: Do NOT add the global flag here — this pattern is used in
// String.replace() across multiple calls, and a global regex retains
// lastIndex state between uses, causing missed replacements.
export const KNOWLEDGE_REGEX_SPECIAL_CHARS_PATTERN = "[.*+?^${}()|[\\]\\\\]";
